'use client';

import * as React from 'react';
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';

import { XIcon } from 'lucide-react';
import { cn } from '@/libs/shadcn';
import { Button } from '../button/button';

function ScrollableWithShadow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = React.useState(false);
  const [isAtBottom, setIsAtBottom] = React.useState(false);

  const checkScrollable = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setIsScrollable(el.scrollHeight > el.clientHeight);
  }, []);

  const checkAtBottom = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 4;
    setIsAtBottom(atBottom);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScrollable();
    checkAtBottom();
    const observer = new ResizeObserver(() => {
      checkScrollable();
      checkAtBottom();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkScrollable, checkAtBottom, children]);

  return (
    <div className="relative">
      <div ref={scrollRef} className={className} onScroll={checkAtBottom}>
        {children}
      </div>
      {isScrollable && !isAtBottom && (
        <div
          className="rounded-md pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-linear-to-t from-primary/12 via-primary/4 to-transparent"
          aria-hidden
        />
      )}
    </div>
  );
}

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        'data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 isolate z-50',
        className
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          /*DEFAULT*/
          // "bg-background data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 ring-foreground/10 grid max-w-[calc(100%-2rem)] gap-4 rounded-xl p-4 text-sm ring-1 duration-100 sm:max-w-sm fixed top-1/2 left-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2 outline-none",

          /*MODIFIED*/
          'bg-background data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 ring-foreground/10 grid max-w-[calc(100%-1.5rem)] gap-6 rounded-xl p-8 text-sm ring-1 duration-100 sm:max-w-140 fixed top-1/2 left-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2 outline-none',
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={
              <button className="absolute rounded-sm top-6 right-6 size-6 text-muted-foreground hover:bg-muted hover:text-primary cursor-pointer">
                <XIcon className="flex-shrink-0" />
                <span className="sr-only">Close</span>
              </button>
            }
          />
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogBody({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <ScrollableWithShadow
      className={cn(
        'flex flex-col gap-4 max-h-[50vh] overflow-y-auto overflow-x-hidden p-1',
        className
      )}
    >
      {children}
    </ScrollableWithShadow>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        /*DEFAULT*/
        // "gap-2 flex flex-col",

        /*MODIFIED*/
        'gap-2 flex flex-col',
        className
      )}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        /*DEFAULT*/
        // "bg-muted/50 -mx-4 -mb-4 rounded-b-xl border-t p-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",

        /*MODIFIED*/
        'flex flex-col-reverse gap-2 sm:gap-4 sm:flex-row sm:justify-end',
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close
          render={<Button variant="outline">Close</Button>}
        />
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        /*DEFAULT*/
        // "text-base leading-none font-medium",

        /*MODIFIED*/
        'text-xl font-semibold leading-none text-foreground',
        className
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        /*DEFAULT*/
        // "text-muted-foreground *:[a]:hover:text-foreground text-sm *:[a]:underline *:[a]:underline-offset-3",

        /*MODIFIED*/
        'text-xs font-normal leading-normal text-muted-foreground',
        className
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogBody,
  DialogTitle,
  DialogTrigger,
};
